import React, {
  FunctionComponent,
  useCallback,
  FormEvent,
  useState,
} from 'react';
import styled from 'styled-components';

import { SubmitButton, Card, CardList } from 'components';
import { Text, Icon, List, ListItem, Link, Heading } from 'components/chroma';

import { LinkType, SingleFieldStepProps } from 'scenes/common/types';
import { TermsAndConditions } from 'scenes/common/flows/policyDetails/types';

const ButtonWrapper = styled.div`
  margin-top: 1.5rem;
`;

const CheckCircle = () => <Icon name="checkCircle" margin={{ right: 5 }} />;

const toNewValue = (value: TermsAndConditions): TermsAndConditions =>
  value === TermsAndConditions.Unspecified
    ? TermsAndConditions.Accepted
    : TermsAndConditions.Unspecified;

const isSelected = (value: TermsAndConditions | undefined): boolean =>
  value === TermsAndConditions.Accepted;

const isValid = (
  termsAndConditions: TermsAndConditions,
  termsAndConditionsForSmallBusiness: TermsAndConditions | undefined
): boolean =>
  termsAndConditionsForSmallBusiness !== undefined
    ? isSelected(termsAndConditions) &&
      isSelected(termsAndConditionsForSmallBusiness)
    : isSelected(termsAndConditions);

const TermsAndConditionsStep: FunctionComponent<
  Pick<SingleFieldStepProps<string>, 'onNext'> & {
    termsAndCondition: TermsAndConditions;
    termsAndConditionForSmallBusiness?: TermsAndConditions;
    termsAndConditionsLinks: LinkType[];
  }
> = ({
  onNext,
  termsAndCondition,
  termsAndConditionForSmallBusiness,
  termsAndConditionsLinks,
}) => {
  const [termsAndConditionsState, setTermsAndConditionsState] = useState(
    termsAndCondition
  );
  const [
    termsAndConditionsForSmallBusinessState,
    setTermsAndConditionsForSmallBusinessState,
  ] = useState(termsAndConditionForSmallBusiness);

  const onSubmit = useCallback(
    (event: FormEvent) => {
      onNext({
        termsAndCondition: termsAndConditionsState,
        ...(termsAndConditionsForSmallBusinessState !== undefined
          ? {
              termsAndConditionForSmallBusiness: termsAndConditionsForSmallBusinessState,
            }
          : {}),
      });
      event.preventDefault();
    },
    [onNext, termsAndConditionsState, termsAndConditionsForSmallBusinessState]
  );

  return (
    <form onSubmit={onSubmit}>
      <Heading size={3} margin={{ top: 2 }}>
        Your obligations under the Terms and Conditions of Coles Insurance are
        explained in the following categories
      </Heading>
      <List margin={{ top: 5, bottom: 7 }}>
        {termsAndConditionsLinks.map((link) => (
          <ListItem key={link.text} keyProp={link.text} margin={{ bottom: 2 }}>
            <Icon color="red" name="angleRight" margin={{ right: 2 }} />
            <Link
              href={link.url}
              target="_blank"
              margin={{ bottom: 0, top: 0 }}
              rel="noopener noreferrer"
            >
              {link.text}
            </Link>
          </ListItem>
        ))}
      </List>

      <CardList marginBottom="1rem">
        <Card
          id="terms-and-conditions-agreement"
          selected={isSelected(termsAndConditionsState)}
          center
          onClick={() => {
            setTermsAndConditionsState(toNewValue(termsAndConditionsState));
          }}
          role="button"
        >
          <CheckCircle />
          <Text>
            I agree to the Terms &amp; Conditions and to receive documents and
            notifications electronically.
          </Text>
        </Card>

        {termsAndConditionsForSmallBusinessState !== undefined && (
          <Card
            id="terms-and-conditions-agreement-for-small-business"
            selected={isSelected(termsAndConditionsForSmallBusinessState)}
            center
            onClick={() =>
              setTermsAndConditionsForSmallBusinessState(
                toNewValue(termsAndConditionsForSmallBusinessState)
              )
            }
            role="button"
          >
            <CheckCircle />
            <Text>
              I declare that I am a small business with an aggregate turnover of
              $2 million or less at the time the policy commences or is renewed
            </Text>
          </Card>
        )}
      </CardList>

      <ButtonWrapper>
        <SubmitButton
          enabled={isValid(
            termsAndConditionsState,
            termsAndConditionsForSmallBusinessState
          )}
        >
          Next
        </SubmitButton>
      </ButtonWrapper>
    </form>
  );
};

export default TermsAndConditionsStep;
